import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LAYOUT, PICTURES } from '../../assets/characters';
import { reducedMotion } from '../../utils/zoom';
import styles from './stylonCharacter.module.css';

/**
 * A STYLON CHARACTER, ALIVE — the portal's StylonCharacter (stylon-salon-portal Components/Assistant, owner 10-23:
 * "alive — like the previous Luna avatar: moving, interacting"), on the business page chat (round 3: "match Luna in
 * the portal"). The approved picture with live eyes and marks on it:
 *   idle      eyes wander now and then, a blink every few seconds, sometimes a wink; the body breathes
 *   typing    eyes look down at the field and follow the cursor across it
 *   thinking  a slow bob, eyes up, quicker blinks, the marks spark
 *   speaking  the body swells with the voice
 *   listening it leans in, eyes wide
 *   happy     a hop and a wink — on a tap, or when something you asked for is done
 *   pointer   on a desk screen the eyes follow the mouse when it is near
 * Reduced motion: still, eyes open.
 *
 * Every one floats at rest on its own ground (a float with a small squash as it lands); Luna is drawn in code — a glossy
 * body whose shape breathes, a light that drifts across it and an orange glow under it — never a picture on a disc
 * (owner 10-23: "still with a container… still not alive, it's a pic"; the portal's e4d3c730a). The shape breathes by
 * transform here (the store animates only transform / opacity / clip-path; the portal morphs its border-radius).
 *
 * The portal's props, plus two for the store: `watchPointer` (false for the small one at the start of every answer —
 * a long conversation would otherwise put a mouse listener on each) and `testId`. Its stylesheet is held to the
 * store's feel rules (theChatMovesOnlyAsFarAsTheFeelRulesAllow): the eyes glide in --feel-slow and the hop takes
 * 300 ms (the portal's are 450 / 600 ms).
 */
const GAZES = [[0, 0], [0, 0], [-1, 0], [1, 0], [-0.6, -0.5], [0.6, -0.5], [0, 0.4]];

export function StylonCharacter({
  kind = 'ai', size = 44, loading = false, speaking = false, listening = false, typing = false,
  typingProgress = 0, speakingAmplitude = 0, happy = false, halfShut = false, neutral = false, interactive = false, rtl = false, className = '',
  watchPointer = true, testId = 'character',
}) {
  const layout = LAYOUT[kind] || LAYOUT.ai;
  const picture = PICTURES[kind] || PICTURES.ai;
  const known = !!LAYOUT[kind];
  const drawn = kind === 'ai' || !PICTURES[kind];
  const still = reducedMotion();
  const [gaze, setGaze] = useState([0, 0]);
  const [lids, setLids] = useState(['open', 'open']);
  const [hop, setHop] = useState(false);
  const [pointer, setPointer] = useState(null);
  const ref = useRef(null);
  const timers = useRef([]);
  const later = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); return id; };

  // Gaze and expression, like the old orb: a calm wander, a blink, now and then a wink; quicker blinks while thinking.
  useEffect(() => {
    if (still || neutral) return undefined;
    let alive = true;
    const wander = () => { if (!alive) return; setGaze(GAZES[Math.floor(Math.random() * GAZES.length)]); later(wander, 3200 + Math.random() * 3600); };
    const blink = () => {
      if (!alive) return;
      const wink = Math.random() < 0.2;
      const side = Math.random() < 0.5 ? 0 : 1;
      setLids(wink ? (side ? ['open', 'wink'] : ['wink', 'open']) : ['shut', 'shut']);
      later(() => alive && setLids(['open', 'open']), wink ? 420 : 110);
      later(blink, loading ? 1800 + Math.random() * 1200 : 3800 + Math.random() * 4200);
    };
    later(wander, 1600 + Math.random() * 1800);
    later(blink, 1200 + Math.random() * 2400);
    return () => { alive = false; timers.current.forEach(clearTimeout); timers.current = []; };
  }, [still, loading, neutral]); // eslint-disable-line react-hooks/exhaustive-deps

  // On a desk screen the eyes follow the mouse when it comes near.
  useEffect(() => {
    if (still || !watchPointer || typeof window === 'undefined' || !window.matchMedia?.('(pointer: fine)')?.matches) return undefined;
    let frame = 0;
    const onMove = (e) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const r = ref.current?.getBoundingClientRect?.();
        if (!r) return;
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        const far = Math.hypot(dx, dy);
        setPointer(far > 420 ? null : [Math.max(-1, Math.min(1, dx / 160)), Math.max(-1, Math.min(1, dy / 160))]);
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => { cancelAnimationFrame(frame); window.removeEventListener('pointermove', onMove); };
  }, [still, watchPointer]);

  const tap = useCallback(() => {
    if (!interactive || still) return;
    setHop(true);
    setLids(['open', 'wink']);
    later(() => { setHop(false); setLids(['open', 'open']); }, 650);
  }, [interactive, still]); // eslint-disable-line react-hooks/exhaustive-deps

  const mood = hop || happy ? 'happy' : loading ? 'thinking' : speaking ? 'speaking' : listening ? 'listening' : typing ? 'typing' : 'idle';
  // Where the eyes look, in percent of the eye's own size: typing follows the cursor, then the mouse, then the wander.
  const across = rtl ? 1 - typingProgress : typingProgress;
  // `neutral` looks straight ahead and does not wander (an error, a muted microphone); `halfShut` lowers the lids.
  const look = neutral ? [0, 0] : typing ? [(across * 2 - 1) * 0.9, 0.7] : mood === 'thinking' ? [0.4, -0.7] : pointer || gaze;
  const lx = (look[0] * 42).toFixed(1);
  const ly = (look[1] * 28).toFixed(1);
  const swell = speaking ? 1 + Math.min(1, speakingAmplitude) * 0.06 : 1;

  return (
    <span
      ref={ref}
      className={`${styles.stage} ${styles[mood] || ''} ${still ? styles.still : ''} ${interactive ? styles.interactive : ''} ${className}`}
      style={{ '--size': `${size}px`, '--ratio': `${layout.h / layout.w}`, '--swell': swell }}
      data-testid={testId}
      data-kind={known ? kind : 'ai'}
      data-mood={mood}
      data-still={still ? 'true' : 'false'}
      data-idle="float"
      aria-hidden="true"
      onClick={interactive ? tap : undefined}
    >
      {/* Every character stands on its own ground: a soft shadow that shrinks as it floats up. */}
      <span className={styles.ground} data-part="ground" />
      <span className={styles.body}>
        {drawn ? (
          // Luna is drawn, not a picture: a glossy body whose shape breathes, a light that drifts across it, an orange
          // glow under it — the old orb's life in her own colours.
          <>
            <span className={styles.glow} data-part="glow" />
            <span className={styles.orb} data-part="orb">
              <span className={styles.shine} data-part="shine" />
            </span>
          </>
        ) : (
          <img className={styles.picture} src={picture} alt="" draggable="false" />
        )}
        {layout.eyes.map((e, i) => (
          <span
            key={i}
            className={styles.eye}
            data-eye={halfShut ? 'half' : (lids[i] || 'open')}
            style={{ left: `${e.x}%`, top: `${e.y}%`, width: `${e.w}%`, height: `${e.h}%`, background: layout.eyeColor, transform: `translate(${lx}%, ${ly}%)` }}
          />
        ))}
      </span>
      {layout.marks.map((m, i) => (
        <span
          key={i}
          className={styles.mark}
          data-mark=""
          style={{ left: `${m.x}%`, top: `${m.y}%`, width: `${m.w}%`, height: `${m.h}%`, '--i': i }}
        >
          <span style={{ '--tilt': `${Math.round((Math.atan2(m.w, m.h) * 180) / Math.PI)}deg` }} />
        </span>
      ))}
    </span>
  );
}

export default StylonCharacter;
